/**
 * CobroFormFields
 * ----------------
 * Layout del formulario de cobro rápido. Componente "tonto": recibe `form`
 * y handlers, no tiene estado propio.
 *
 * Phase QR2 — extraído de CobroQuickDialog para reducir su complejidad.
 */
import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { PatientCombobox } from '../PatientCombobox';
import { PAYMENT_METHODS } from '../../lib/cobroQuickHelpers';
import { amountOptionsForConcept, isDevolucionConcept } from '../../lib/cobroPresets';
import { ConceptoCobroField } from './ConceptoCobroField';

export function CobroFormFields({ form, patients, setField, selectPatient }) {
  const setConcept = (value, importeSugerido) => {
    setField('concept', value);
    if (importeSugerido !== null && importeSugerido !== undefined) setField('amount', importeSugerido);
    // Devolución (dirección, 2026-09-08): nunca lleva IVA.
    if (isDevolucionConcept(value)) setField('vat_applied', false);
  };
  const amountOptions = amountOptionsForConcept(form.concept);
  const esDevolucion = isDevolucionConcept(form.concept);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <Label>Paciente (autocomp.)</Label>
        <PatientCombobox
          value={form.patient_id}
          onChange={selectPatient}
          patients={patients}
          placeholder="— o escribe manualmente —"
          dataTestid="cobro-quick-patient-select"
        />
      </div>
      <div>
        {/* Sin la de hoy por defecto (clínica, 30/9/2026): se elige siempre. */}
        <Label>Fecha *</Label>
        <Input
          type="date"
          value={form.date}
          onChange={(e) => setField('date', e.target.value)}
          data-testid="cobro-quick-date"
        />
      </div>
      <div className="md:col-span-2">
        <Label>Nombre del paciente *</Label>
        <Input
          value={form.patient_name}
          onChange={(e) => setField('patient_name', e.target.value)}
          data-testid="cobro-quick-patient-name"
        />
      </div>
      <div>
        <Label>NIF / DNI</Label>
        <Input
          value={form.nif}
          onChange={(e) => setField('nif', e.target.value)}
          data-testid="cobro-quick-nif"
        />
      </div>
      <div>
        <Label>Dirección</Label>
        <Input
          value={form.address}
          onChange={(e) => setField('address', e.target.value)}
          data-testid="cobro-quick-address"
        />
      </div>
      <div className="md:col-span-2">
        <Label>Concepto *</Label>
        <ConceptoCobroField
          value={form.concept}
          onChange={setConcept}
          dataTestid="cobro-quick-concept"
        />
      </div>
      <div>
        <Label>{esDevolucion ? 'Importe devuelto (€) *' : 'Importe que paga el paciente (€) *'}</Label>
        <Input
          type="number"
          list="cobro-quick-amount-options"
          step="0.01"
          min="0"
          value={form.amount}
          onChange={(e) => setField('amount', e.target.value)}
          data-testid="cobro-quick-amount"
        />
        <datalist id="cobro-quick-amount-options">
          {amountOptions.map((amount) => <option key={amount} value={amount} />)}
        </datalist>
      </div>
      <div className="flex flex-col gap-2">
        <Label>IVA</Label>
        {esDevolucion ? (
          <p className="text-sm text-muted-foreground pt-2" data-testid="cobro-quick-sin-iva">Sin IVA: es una devolución.</p>
        ) : (
          <label className="flex items-center gap-2 pt-2 cursor-pointer">
            <Checkbox
              checked={form.vat_applied}
              onCheckedChange={(v) => setField('vat_applied', !!v)}
              data-testid="cobro-quick-vat"
            />
            <span className="text-sm">Con IVA 21% (incluido en el importe)</span>
          </label>
        )}
      </div>
      <div className="md:col-span-2">
        <Label>Método de pago *</Label>
        <Select value={form.payment_method} onValueChange={(v) => setField('payment_method', v)}>
          <SelectTrigger data-testid="cobro-quick-method"><SelectValue placeholder="— Elige el método —" /></SelectTrigger>
          <SelectContent>
            {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label>Notas (opcional)</Label>
        <Textarea
          rows={2}
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
        />
      </div>
    </div>
  );
}
